import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Code } from 'lucide-react';
import { SERVICES, TECH_LOGOS, CONTACT } from '../data/mock';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20 relative overflow-hidden">
      <SEO
        title="Elite Full-Stack Engineering Services | React, Node, DevOps & Cloud"
        description="Comprehensive full-stack engineering capabilities: Frontend (React, Angular), Backend (Node.js, Python), DevOps (Kubernetes, AWS), Mobile, API Integration. 150+ enterprise solutions delivered."
        keywords="full-stack development services, react development, nodejs development, devops consulting, cloud development, mobile app development"
        url="/services"
      />

      {/* Geometric Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="geometric-pattern" style={{ top: '5%', right: '10%' }} />
        <div className="geometric-pattern" style={{ bottom: '10%', left: '5%', animationDelay: '2s' }} />

        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration medium floating" style={{ top: '12%', right: '6%', animationDelay: '1s' }} />
        <div className="bento-box-decoration small floating" style={{ bottom: '15%', left: '8%', animationDelay: '3s' }} />
        <div className="luxury-gradient-overlay" style={{ top: '20%', right: '15%', width: '300px', height: '300px' }} />
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-50/30 via-transparent to-purple-50/30 pointer-events-none" />
        <div className="container max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block mb-6">
              <span className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 text-[#4C3362] text-sm font-bold border-2 border-purple-200">
                10 Specialized Engineering Disciplines
              </span>
            </div>
            <h1 className="display-lg mb-8">
              Elite <span className="gradient-text">Engineering Capabilities</span>
            </h1>
            <p className="text-xl text-slate-700 font-medium leading-relaxed max-w-4xl mx-auto">
              Comprehensive full-stack solutions spanning frontend excellence to backend architecture,
              DevOps automation to cloud-native infrastructure. Select specialized capabilities or comprehensive solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Mosaic Grid */}
      <section className="pb-12 pt-0 bg-white relative">
        <div className="container max-w-7xl">
          <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="bento-card block h-full group cursor-pointer"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg transition-all group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, ${service.color}30, ${service.color}15)`,
                        border: `2px solid ${service.color}40`
                      }}
                    >
                      <img
                        src={TECH_LOGOS[service.technologies[0]?.logo] || TECH_LOGOS.react}
                        alt=""
                        className="w-8 h-8"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-1 group-hover:text-[#4C3362] transition-colors" style={{ color: service.color }}>
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-4 leading-relaxed text-sm">
                    {service.shortDesc}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.technologies.slice(0, 4).map((tech, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5 border border-slate-200"
                      >
                        <img src={TECH_LOGOS[tech.logo]} alt={tech.name} className="w-3.5 h-3.5" />
                        <span className="text-slate-700 font-medium">{tech.name}</span>
                      </div>
                    ))}
                  </div>

                  <div
                    className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all mt-auto"
                    style={{ color: service.color }}
                  >
                    Explore Capability <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-br from-[#4C3362] via-[#3C284D] to-[#2E2040] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Architect Your Solution</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Discuss your technical requirements with elite engineering specialists.
              Strategic consultation without conventional sales approaches.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#4C3362] font-bold px-10 py-5 rounded-xl hover:shadow-2xl hover:-translate-y-2 transition-all text-lg"
            >
              Get Started
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container max-w-5xl">
          <ContactForm ctaText="Ready to Elevate Your Technical Capabilities?" />
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
